import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { JOURNAL_LOGO_URL } from "@/lib/constants";
import { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function AdminLogin() {
  const { t } = useI18n();
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    setTimeout(() => navigate("/admin", { replace: true }), 0);
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (ex) {
      setErr(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={JOURNAL_LOGO_URL} alt="CSR" className="h-20 mx-auto mb-6" />
          <div className="font-serif text-2xl text-[#001F3F]">{t("login.title")}</div>
          <div className="w-10 h-[2px] bg-[#2BA6A6] mx-auto mt-3"></div>
        </div>
        <form onSubmit={submit} className="bg-white border border-gray-200 p-8 space-y-5" data-testid="admin-login-form">
          <div className="flex items-center gap-2 text-[#001F3F]">
            <Lock className="w-4 h-4 text-[#2BA6A6]" />
            <div className="font-sans text-[11px] uppercase tracking-[0.2em] font-semibold">{t("login.heading")}</div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">{t("login.email")}</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
              data-testid="admin-login-email"
            />
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[#001F3F] font-sans font-semibold mb-2">{t("login.password")}</label>
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 bg-white border border-gray-200 text-sm font-sans focus:outline-none focus:border-[#2BA6A6]"
              data-testid="admin-login-password"
            />
          </div>
          {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm font-sans p-3">{err}</div>}
          <button
            type="submit" disabled={loading}
            className="w-full bg-[#001F3F] text-white px-5 py-3 text-sm font-sans uppercase tracking-[0.15em] font-semibold hover:bg-[#2BA6A6] transition-colors disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? t("login.signing_in") : t("login.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
